import React, { useEffect, useState } from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import api from '../../../helpers/api';
import sources from '../../../helpers/sources';
import Link from '../../components/link';
import notification from '../../../helpers/notification';
import currencyFormatter from '../../../helpers/currency_formatter';
import Button from '../../components/button';
import Switch from '../../components/switch';
import replaceItem from '../../../helpers/replace_item';
import Dropdown from '../../components/dropdown';

interface Website {
  domain: string;
}

const ManageBookAndPay = () => {
  const $rootScope = useAngularService('$rootScope');
  const $modal = useAngularService('$modal');
  const MasqueradingService = useAngularService('MasqueradingService');
  const AdminUserService = useAngularService('AdminUserService');
  const IntercomService = useAngularService('IntercomService');
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [website, setWebsite] = useState<Website>();
  const [loading, setLoading] = useState(false);
  const currency = $rootScope.gym.currency_symbol;
  const isMasquerading = MasqueradingService.isMasquerading();

  const loadMemberships = () => {
    setLoading(true);
    const url = `${sources.membershipPlans($rootScope.gym.id)}/manage_book_and_pay`;

    api.get(url, { count: 1000 }).then(
      (data) => {
        setLoading(false);
        setMembershipPlans(data);
      },
      () => {
        setLoading(false);
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  const loadWebsite = () => {
    const url = sources.website($rootScope.gym.id);
    api.get(url).then(
      (data) => {
        setWebsite(data);
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  useEffect(() => {
    loadMemberships();
    loadWebsite();
  }, []);

  const onValueToggle = function (value, key, index) {
    const prevValue = membershipPlans[index];
    const newValue = { ...prevValue, [key]: value };
    setMembershipPlans((plans) => replaceItem(plans, newValue, index));

    const url = sources.membershipPlan($rootScope.gym.id, newValue.id);
    api.put(url, { membership_plan: newValue }).then(
      (updated) => {
        setMembershipPlans((plans) => replaceItem(plans, { ...newValue, ...updated }, index));
      },
      () => {
        setMembershipPlans((plans) => replaceItem(plans, prevValue, index));
        notification.error('There was an error processing the request.');
      },
    );
  };

  const manage = function (plan) {
    const modalInstance = $modal.open({
      template: require('templates/gyms/membership_plans/manage_membership_plan_book_and_pay_modal.html.slim'),
      resolve: {
        plan: () => ({ ...plan }),
      },
      controller: 'ManageMembershipPlanBookAndPayCtrl',
    });
    modalInstance.result.then(() => {
      loadMemberships();
    });
  };

  const isPaymentOnlyPlan = (plan) => {
    return (
      ['start_on_next_upcoming', 'start_relative_to_purchase_date'].indexOf(
        plan.default_start_date_strategy,
      ) >= 0 ||
      plan.specified_start_date ||
      !plan.restrict_access_by_event_type
    );
  };

  const getSampleUrl = function (plan) {
    if (!website) return;
    const poParam = isPaymentOnlyPlan(plan) ? '?ct=po' : '';
    return `/websites/${website.domain}/book_and_pay/offers/${plan.uuid}/carts/sample_complete${poParam}`;
  };

  const displayCopyUrlModal = function (plan) {
    $modal.open({
      template: require('templates/gyms/membership_plans/copy_book_and_pay_url_modal.html.slim'),
      resolve: {
        plan: () => plan,
      },
      controller: 'CopyBookAndPayUrlModalCtrl',
    });
  };

  const onActionSelect = (action, plan) => {
    if (action === 'copy_url') {
      displayCopyUrlModal(plan);
    }
  };

  useEffect(() => {
    IntercomService.track('visited-manage-book-and-pay-page');
  }, []);

  return (
    <section data-autoscroll="false" className="content-section manage-memberships">
      <header className="page-header page-header--multiple-rows">
        <div className="page-header__tabs">
          <Link className="page-header__item" route="gym.membership-plans.list">
            Membership plans
          </Link>

          {isMasquerading && AdminUserService.has('admin_unarchive_records') && (
            <Link
              className="page-header__item"
              route="gym.membership-plans.archived-membership-plans"
            >
              <i className="icon-document-delete" /> Archived
            </Link>
          )}

          <button type="button" className="page-header__item page-header__item--active">
            Manage Checkout plans
          </button>
        </div>
      </header>

      <div className="container">
        <div className="worx-filled-box content-box">
          <p>
            Clubworx Checkout is a great way to enable on-the-spot payments and bookings for your
            classes. There are two Checkout options to choose from:
          </p>
          <strong>Book &amp; Pay Checkout</strong>
          <ul>
            <li>
              Book &amp; Pay allows your customers to book into a class, and pay for that class at
              the same time. It's the perfect option for pay-as-you-go classes.
            </li>
            <li>
              To enable Book &amp; Pay Checkout, a membership plan must be set to take a single
              payment only, and it must also be restricted by one or more event type.
            </li>
          </ul>
          <br />
          <strong>Payment-Only Checkout</strong>
          <ul>
            <li>
              Payment-Only makes it easy to accept one-off payments from customers in real time. It
              allows you to sign up customers on the spot who don't need to attend a class at the
              same time.
            </li>
            <li>
              To enable Payment-Only Checkout, the membership plan just needs to be set to take a
              single payment only.
            </li>
          </ul>
          <p>
            Once Checkout is enabled for a plan, you'll be given a link that gives customers a quick
            and easy way to make one-off payments using their credit card.
          </p>
          <p>
            You can also choose to collect Marketing Information for any new members that sign up
            via Checkout, and you can also allow new customers to sign up via Book &amp; Pay from
            your website calendar, using the "Link from Website Calendar?" option.
          </p>
        </div>
        <main className="sub-content">
          <table className="table worx-table worx-table--v2">
            <thead>
              <tr>
                <th>Name</th>
                <th>Payment Amount</th>
                <th className="text-center">Enabled?</th>
                <th className="text-center">Link from Website Calendar?</th>
                <th className="text-center">Collect Marketing Info</th>
                <th>After Successful Purchase</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {membershipPlans.map((membershipPlan, index) => (
                <tr key={membershipPlan.id}>
                  <td>
                    <span className="worx-table-primary-text">
                      <Link
                        className="member"
                        route="gym.membership-plans.show"
                        routeParams={{ membershipPlanId: membershipPlan.id }}
                      >
                        {membershipPlan.label}
                      </Link>
                      <div className="margin-top-4">{membershipPlan.category?.label}</div>
                    </span>
                  </td>
                  <td>{currencyFormatter(membershipPlan.first_amount, currency)}</td>
                  <td className="text-center">
                    <Switch
                      checked={membershipPlan.book_and_pay_enabled}
                      disabled={!membershipPlan.book_and_pay_allowed}
                      onChange={(value) => onValueToggle(value, 'book_and_pay_enabled', index)}
                    />
                  </td>
                  <td className="text-center">
                    {membershipPlan.event_type_ids.length > 0 && (
                      <Switch
                        checked={membershipPlan.allow_book_and_pay_from_calendar}
                        disabled={!membershipPlan.book_and_pay_allowed}
                        onChange={(value) =>
                          onValueToggle(value, 'allow_book_and_pay_from_calendar', index)
                        }
                      />
                    )}
                  </td>
                  <td className="text-center">
                    <Switch
                      checked={membershipPlan.collect_book_and_pay_marketing_info}
                      disabled={!membershipPlan.book_and_pay_enabled}
                      onChange={(value) =>
                        onValueToggle(value, 'collect_book_and_pay_marketing_info', index)
                      }
                    />
                  </td>
                  <td>
                    {membershipPlan.uses_default_book_and_pay_complete_page_settings
                      ? 'Return to calendar / new purchase page'
                      : 'Redirect to custom URL'}
                    {membershipPlan.book_and_pay_enabled && (
                      <div>
                        <Button variant="link" onClick={() => manage(membershipPlan)}>
                          Manage
                        </Button>
                        <span> | </span>
                        <a
                          href={getSampleUrl(membershipPlan)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Sample receipt
                        </a>
                      </div>
                    )}
                  </td>
                  <td>
                    {membershipPlan.book_and_pay_enabled ? (
                      <div className="text-right">
                        <Dropdown
                          options={[
                            {
                              value: 'copy_url',
                              label: 'Copy direct link to clipboard',
                            },
                          ]}
                          onSelect={(action) => onActionSelect(action, membershipPlan)}
                          buttonType="icon-more"
                        />
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!membershipPlans.length && (
                <tr>
                  <td colSpan={7}>{loading ? 'Loading...' : 'No results'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </main>
      </div>
    </section>
  );
};

angular.module('gym').value('ManageBookAndPay', ManageBookAndPay);
